/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '../../services/api.config';
import { useDataFetchIndicator } from '..';

export type DocumentUploadResponse = {
  success: boolean;
  errorMessage: string;
  documentID: string;
};

type HookProps = {
  api: vp.Api;
};

type ReturnTuple = [
  DocumentUploadResponse | undefined,
  (requestParams: vp.DocumentUploadRequest, bearerToken: string) => void,
  (requestParams: vp.DocumentUploadRequest, bearerToken: string) => Promise<vp.DocumentUploadResponse>,
  () => void,
];

function usePostDocumentUpload({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<vp.DocumentUploadRequest>();
  const [response, setResponse] = useState<DocumentUploadResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [bearerToken, setBearerToken] = useState<string>('');

  useEffect(() => {
    const sendRequest = async (requestParams: vp.DocumentUploadRequest) => {
      setDataFetchingInfo(true, 'uploading document', 'fullScreen');

      const { Success, ErrorMessage, DocumentID } = await api.postAccountDocumentUpload(
        buildParams(requestParams),
        bearerToken,
        source,
      );

      const res = {
        success: Success,
        errorMessage: ErrorMessage || '',
        documentID: DocumentID || '',
      };

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams?: vp.DocumentUploadRequest, _bearerToken?: string) => {
      setParams(requestParams);
      setBearerToken(_bearerToken ?? '');
      setSendRequest(true);
    },
    (requestParams: vp.DocumentUploadRequest, _bearerToken: string) =>
      api.postAccountDocumentUpload(buildParams(requestParams), _bearerToken, source),
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
  ];

  function buildParams(requestParams: vp.DocumentUploadRequest): vp.DocumentUploadRequest {
    const { DocumentContent, DocumentName, DocumentType } = requestParams;
    // add information for user
    return vp.removeEmptyProperty<vp.DocumentUploadRequest>({
      DocumentContent,
      DocumentName,
      DocumentType,
    });
  }
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(usePostDocumentUpload);
